<template>
  <div class="card-note">
    <div class="card-note__top">
      <div class="card-note__creator">
        <img
          v-if="creatorPhoto"
          :src="creatorPhoto"
          class="card-note__image"
          heigt="24"
          width="24"
        />
        {{ createdBy || typeLabel }}
      </div>
      <div class="card-note__created-at">
        {{ createdAt }}
      </div>
    </div>
    <div v-if="createdBy" class="card-note__type">
      {{ typeLabel }}
    </div>
    <div class="card-note__content">
      <template v-if="isViewingAppointment">
        <p>
          <strong>{{ $t('DASHBOARD.CARD.EXPANDED.NOTES.DATE') }}</strong
          >:
          {{ getLocalDateString(cardDynamicData.from) }}
          {{ cardDynamicData.until ? ` - ${getLocalDateString(cardDynamicData.until)}` : '' }}
        </p>
        <p v-if="cardDynamicData.location">
          <strong>{{ $t('DASHBOARD.CARD.EXPANDED.NOTES.LOCATION') }}</strong
          >:
          {{ cardDynamicData.location }}
        </p>
        <p v-if="content">
          <strong>{{ $t('DASHBOARD.CARD.EXPANDED.SECTION.NOTES') }}</strong
          >:
          {{ content }}
        </p>
      </template>

      <template v-else-if="isCallLog">
        {{ content }}
        <hr />
        <p class="card-note__additional-info">
          {{ additionalInfo }}
        </p>
      </template>

      <template v-else>
        {{ content }}
      </template>
    </div>
  </div>
</template>

<script>
export default {
  name: 'CardNote',
  props: {
    deal: {
      type: Object,
      required: true,
    },
    cardDynamicData: {
      type: Object,
      required: true,
    },
  },
  computed: {
    type() {
      return this.cardDynamicData?.type ?? this.cardDynamicData?.category;
    },
    isCallLog() {
      return this.type === 'call_log';
    },
    isResetComment() {
      return this.type === 'reset_comment';
    },
    isViewingAppointment() {
      return this.type === 'appointment_time_slot';
    },
    creatorPhoto() {
      return this.cardDynamicData?.creator_photo ?? null;
    },
    createdBy() {
      return this.cardDynamicData?.created_by ?? null;
    },
    createdAt() {
      return this.cardDynamicData?.created_at
        ? this.getLocalDateString(this.cardDynamicData.created_at)
        : null;
    },
    typeLabel() {
      return this.$t(`DASHBOARD.CARD.EXPANDED.NOTES.${this.type.toUpperCase()}`);
    },
    content() {
      return this.isCallLog || this.isResetComment || this.isViewingAppointment
        ? this.cardDynamicData?.note
        : this.cardDynamicData?.content;
    },
    additionalInfo() {
      const hours = Number(this.cardDynamicData.duration.substr(0, 2));
      const minutes = Number(this.cardDynamicData.duration.substr(3, 2));
      const seconds = Number(this.cardDynamicData.duration.substr(6, 2));

      if (!this.isCallLog) return null;

      const duration = [
        this.$t('GENERAL.DURATION'),
        ...(hours ? [`${hours} ${this.$tc('GENERAL.HOURS', hours)}`] : []),
        ...(hours && minutes ? [this.$t('GENERAL.AND')] : []),
        ...(minutes ? [`${minutes} ${this.$tc('GENERAL.MINUTES', minutes)}`] : []),
        ...(seconds && !minutes && !hours
          ? [this.$t('DASHBOARD.CARD.EXPANDED.NOTES.LESS_THAN_A_MINUTE')]
          : []),
      ].join(' ');

      return [
        this.cardDynamicData.info,
        this.cardDynamicData.reason,
        this.cardDynamicData.outcome,
        ...(hours || minutes || seconds ? [duration] : []),
      ].join(' | ');
    },
  },
  methods: {
    getLocalDateString(string) {
      const date = new Date(string);

      const germanDateString = date.toLocaleDateString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });

      const germanTimeString = date.toLocaleTimeString('de-DE', {
        hour: '2-digit',
        minute: '2-digit',
      });

      return this.$t('DASHBOARD.CARD.EXPANDED.NOTES.CREATED_AT', {
        germanDateString,
        germanTimeString,
      });
    },
  },
};
</script>
<style lang="scss" scoped>
@import '@/styles/mixins.scss';

.card-note {
  > div:not(:last-child) {
    margin-bottom: $sp-s;
  }

  &__top,
  &__creator {
    align-items: center;
    display: flex;
    gap: $sp-s;
    justify-content: space-between;
  }

  &__image {
    background-color: $primary-bg;
    border-radius: 50%;
    height: 24px;
    object-fit: cover;
    overflow: hidden;
    width: 24px;
  }

  &__creator {
    @include font('DS-100');
    font-weight: 600;
  }

  &__created-at,
  &__type {
    @include font('DS-80');
    color: getShade($quaternary-color, 80);
  }

  &__content {
    @include font('DS-90');
    white-space: pre-wrap;
    word-wrap: break-word;

    strong {
      font-weight: 600;
    }

    p:not(:first-child) {
      margin-top: $sp-xs;
    }

    hr {
      height: 1px;
      margin: $sp-s + $sp-xs 0;
      background: getShade($quaternary-color, 50);

      @include for('tablet') {
        margin: $sp-m 0;
      }
    }
  }

  &__additional-info {
    @include font('DS-80');
    color: getShade($quaternary-color, 80);
  }
}
</style>
